import { Fragment, type ElementType } from 'react';

/**
 * A page title in the largest type the page has, rising a word at a time out
 * of its own baseline, with one word marked in ink once the line has landed.
 *
 * `mark` picks the word: the last one with a letter in it by default (so the
 * ink lands on "séances" and not on a question mark after it), or none.
 */
export function Display({
  text,
  mark = 'last',
  as: Tag = 'h1',
  className = '',
}: {
  text: string;
  mark?: 'last' | 'none';
  as?: ElementType;
  className?: string;
}) {
  const words = text.split(' ');
  let marked = -1;
  if (mark === 'last') {
    words.forEach((word, index) => {
      if (/\p{L}/u.test(word)) marked = index;
    });
  }
  return (
    <Tag className={`display ${className}`.trim()}>
      {words.map((word, index) => (
        <Fragment key={index}>
          <span className="display__mask">
            <span className="display__word" style={{ animationDelay: `${40 + index * 70}ms` }}>
              {index === marked ? <Marked word={word} /> : word}
            </span>
          </span>
          {index < words.length - 1 ? ' ' : null}
        </Fragment>
      ))}
    </Tag>
  );
}

/** The highlight covers the word, not the punctuation stuck to it. */
function Marked({ word }: { word: string }) {
  const [, letters = word, rest = ''] = /^(.*\p{L})(.*)$/u.exec(word) ?? [];
  return (
    <>
      <span className="display__mark">{letters}</span>
      {rest}
    </>
  );
}
